import type { ApprovalAttributes } from '@/types/approval';
import {
  formatSerpText,
  SERP_NOT_LOADED_MESSAGE,
  type SerpDocumentRef,
} from '@/utils/approval/serpDocument';

/** SERP 매장 변경 — 레거시 appr_edit_forms_serp_shop_change */
export const SERP_STORE_CHANGE_TYPE = 162;

export const isSerpStoreChangeType = (apvType: number): boolean =>
  apvType === SERP_STORE_CHANGE_TYPE;

/** 표 행 순서·라벨 — 값은 원문의 `BEF_{key}`·`AFT_{key}` */
export const SERP_STORE_CHANGE_ROWS = [
  { key: 'BRAND_NM', label: '브랜드' },
  { key: 'SHOP_CD', label: '매장코드' },
  { key: 'SHOP_NM', label: '매장명(정식)' },
  { key: 'SHOP_ABBR_NM', label: '매장명(약칭)' },
  { key: 'BRN', label: '사업자번호' },
  { key: 'TRN', label: '(종번호)' },
  { key: 'REP_NM', label: '대표자' },
  { key: 'BT_NM', label: '업태' },
  { key: 'BI_NM', label: '종목' },
  { key: 'RRN', label: '법인번호/생년월일' },
  { key: 'BRN_STA_DE', label: '사업자번호 시작일자' },
  { key: 'ZIP', label: '우편번호' },
  { key: 'BSC_ADDR', label: '기본주소' },
  { key: 'DTL_ADDR', label: '상세주소' },
  { key: 'TEL_NO', label: '전화번호(유선)' },
  { key: 'CEL_NO', label: '전화번호(핸드폰)' },
  { key: 'SHOP_TRD_TYP', label: '매장거래유형' },
  { key: 'SHOP_MGMT_TYP', label: '매장관리유형' },
  { key: 'PROD_TRD_TYP', label: '상품거래유형' },
  { key: 'SHOP_CTRT_TYP', label: '매장계약유형' },
  { key: 'SHOP_SALE_DIV', label: '판매구분' },
  { key: 'DN_DIV', label: '유통망구분' },
  { key: 'GI_PSBT_YN', label: '출고가능여부' },
  { key: 'SALE_PSBT_YN', label: '판매가능여부' },
  { key: 'RF_DIV', label: 'RF 구분' },
  { key: 'SAP_CH', label: 'SAP 채널구분' },
  { key: 'SAP_MOV_TYP', label: 'SAP 이동유형' },
  { key: 'CTB_TYP', label: '수금유형' },
] as const;

/** 계좌 행 항목 — 값은 계좌별 `BEF_{key}`·`AFT_{key}` */
export const SERP_STORE_CHANGE_ACCOUNT_ITEMS = [
  { key: 'DEPR_NM', label: '예금주' },
  { key: 'BANK_NM', label: '은행' },
  { key: 'BANK_AC_NO', label: '계좌번호' },
  { key: 'CTB_FORM', label: '지급/수금형태' },
  { key: 'PMT_TYP', label: '지급유형' },
  { key: 'BANK_CTRY_CD', label: '은행국가' },
  { key: 'USE_YN', label: '사용유무' },
] as const;

export type SerpStoreChangeAccount = Record<string, unknown>;

/** EAI 응답 원문 — 저장 attributes.data 에 손대지 않고 그대로 보관 */
export type SerpStoreChangeSource = {
  APRV_TITLE?: string | null;
  DTL_LIST?: SerpStoreChangeAccount[] | null;
  [key: string]: unknown;
};

export type SerpStoreChangeDraftSource = {
  code: number;
  attributes: SerpDocumentRef;
  display: { source?: SerpStoreChangeSource | null };
};

export type SerpStoreChangeState = SerpDocumentRef & {
  source: SerpStoreChangeSource;
  /** SERP 응답으로 자동 입력한 제목 — 이탈 경고는 이 값과 비교. 편집은 미사용 */
  autoSubject: string;
};

export const buildSerpStoreChangeSubject = (
  source: SerpStoreChangeSource,
): string => formatSerpText(source.APRV_TITLE);

/** 레거시는 변경후 키가 응답에 있을 때만 글자를 비교 */
export const isSerpStoreFieldChanged = (
  source: SerpStoreChangeSource,
  key: string,
): boolean =>
  Object.prototype.hasOwnProperty.call(source, `AFT_${key}`) &&
  formatSerpText(source[`AFT_${key}`]) !== formatSerpText(source[`BEF_${key}`]);

/** 계좌 행은 레거시가 원문 값을 그대로(!==) 비교 */
export const isSerpAccountFieldChanged = (
  account: SerpStoreChangeAccount,
  key: string,
): boolean => account[`AFT_${key}`] !== account[`BEF_${key}`];

export const getSerpStoreAccounts = (
  source: SerpStoreChangeSource,
): SerpStoreChangeAccount[] =>
  Array.isArray(source.DTL_LIST)
    ? source.DTL_LIST.filter(
        (row): row is SerpStoreChangeAccount =>
          !!row && typeof row === 'object',
      )
    : [];

export const checkSerpStoreChangeBeforeSave = (
  state: SerpStoreChangeState | null | undefined,
): string | null => (state?.source ? null : SERP_NOT_LOADED_MESSAGE);

export const buildSerpStoreChangeAttributes = (
  state: SerpStoreChangeState | null | undefined,
  content: string,
): ApprovalAttributes | null =>
  state?.source
    ? {
        typeCode: state.typeCode,
        sequence: state.sequence,
        content,
        data: state.source,
      }
    : null;
